/**
 * Admin app environment configuration + validation.
 *
 * Mirrors apps/portal/src/config/environment.ts intentionally — admin and
 * portal share the same Supabase project and hit the same backend URLs.
 * Both apps load .env from the repo root via vite.config.ts's envDir setting.
 *
 * IMPORTANT: every read of import.meta.env uses DOT access with a literal
 * property name (import.meta.env.VITE_SUPABASE_URL). Vite's static-analysis
 * substitution at build time only handles dot access — bracket access with
 * a runtime variable like import.meta.env[envVar] does NOT get inlined and
 * will read undefined at runtime when the build provides env via process.env
 * (e.g. Cloudflare Pages) instead of an .env file on disk.
 */

export function validateEnvironment(): void {
  const missing: string[] = [];
  if (!import.meta.env.VITE_SUPABASE_URL) missing.push('VITE_SUPABASE_URL');
  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) missing.push('VITE_SUPABASE_ANON_KEY');
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(', ')}\n\n` +
        'Please ensure these variables are set (repo-root .env for local dev, ' +
        'Cloudflare Pages env vars for production):\n' +
        missing.map(v => `${v}=your_value_here`).join('\n')
    );
  }
}

export const env = {
  supabase: {
    url: import.meta.env.VITE_SUPABASE_URL as string,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    /**
     * Optional read-replica load-balancer URL. When unset (or
     * VITE_ADMIN_USE_REPLICA != 'true'), the admin app issues all reads
     * against `url` (primary). See PR-E1 for the routing layer.
     */
    readUrl: import.meta.env.VITE_SUPABASE_READ_URL as string | undefined
  },
  app: {
    name: 'admin',
    version: import.meta.env.VITE_APP_VERSION as string | undefined
  }
} as const;
